// @flow
import flatMap from 'lodash/fp/flatMap';

import { mergeWith } from './array';

export type ObjectFeature = $ReadOnly<{|
  name: string,
  // recursive type
  // eslint-disable-next-line no-use-before-define
  items: $ReadOnlyArray<Feature>,
|}>;

export type Feature = string | ObjectFeature;

export const items = (feature: Feature | null): $ReadOnlyArray<Feature> =>
  typeof feature === 'object' && feature ? feature.items : [];

// shallow
export const featuresWithoutFeature = (
  features: $ReadOnlyArray<Feature>,
  name: string,
): $ReadOnlyArray<Feature> =>
  features.filter((f) =>
    typeof f === 'string' ? f !== name : f.name !== name,
  );

const flattenedFeature = (feature: Feature): $ReadOnlyArray<string> =>
  typeof feature === 'string'
    ? [feature] // eslint-disable-next-line no-use-before-define
    : flattenedFeatureList(items(feature));

export const flattenedFeatureList = (
  features: $ReadOnlyArray<Feature>,
): $ReadOnlyArray<string> => flatMap(flattenedFeature, features);

export const featureWithItem = (feature: Feature, item: Feature): Feature =>
  typeof feature === 'string'
    ? feature
    : {
        ...feature,
        items: [...feature.items, item],
      };

export const mergedFeatureLists = (
  a: $ReadOnlyArray<Feature> = [],
  b: $ReadOnlyArray<Feature> = [],
): $ReadOnlyArray<Feature> =>
  mergeWith(
    (needle, haystack) =>
      haystack.find(
        (f) =>
          f === needle || (f.name && needle.name && f.name === needle.name),
      ),
    (featureA, featureB) => {
      if (typeof featureA === 'string' && typeof featureB === 'string')
        return featureA;
      if (typeof featureA === 'string')
        return featureWithItem(featureB, featureA);
      if (typeof featureB === 'string')
        return featureWithItem(featureA, featureB);

      return {
        ...featureA,
        items: mergedFeatureLists(featureA.items, featureB.items),
      };
    },
  )(a, b);

export const flattenedFeatureListWithParent = (
  features: $ReadOnlyArray<Feature>,
): $ReadOnlyArray<[string | null, string]> =>
  features.flatMap((feature) =>
    typeof feature === 'string'
      ? [[null, feature]]
      : flattenedFeatureListWithParent(feature.items).map(([parent, f]) => [
          parent || feature.name,
          f,
        ]),
  );
